/**
 * Raw compiled level JSON -> the `Level` the simulation consumes.
 * Applies the documented defaults (docs/02 3.4, 3.5; docs/03 1.1).
 */
import type { Mode } from '../colors/mix.ts';
import type { Rotation, TileId } from '../sim/tiles.ts';
import type { Level, Placement } from '../sim/types.ts';

interface RawPlacement {
  x: number;
  y: number;
  tile: string;
  rotation?: number;
  locked?: boolean;
  color?: string;
  cans?: number;
  every?: number;
  start?: number;
  order?: { color: string; cans: number };
}

export interface RawLevel {
  id: string;
  title: string;
  mode: string;
  grid: { width: number; height: number };
  tick_ms?: number;
  max_ticks?: number;
  fixed: RawPlacement[];
  starters?: RawPlacement[];
  inventory: Record<string, number | 'inf'>;
  hint?: string;
  tutorial?: string[];
  reference_solution?: RawPlacement[];
}

export function toPlacement(r: RawPlacement): Placement {
  const p: Placement = {
    x: r.x,
    y: r.y,
    tile: r.tile as TileId,
    rotation: ((r.rotation ?? 0) % 360) as Rotation,
  };
  if (r.locked !== undefined) p.locked = r.locked;
  if (r.color !== undefined) p.color = r.color;
  if (r.cans !== undefined) p.cans = r.cans;
  if (r.every !== undefined) p.every = r.every;
  if (r.start !== undefined) p.start = r.start;
  if (r.order !== undefined) p.order = r.order;
  return p;
}

export function loadLevel(raw: RawLevel): Level {
  const fixed = raw.fixed.map(toPlacement);
  const starters = (raw.starters ?? []).map(toPlacement);

  // docs/02 3.5 default: min(400, last_emission + 2*w*h + 10).
  const lastEmission = fixed
    .filter((p) => p.tile === 'vat')
    .reduce((max, v) => {
      const start = v.start ?? 1;
      const every = v.every ?? 1;
      const cans = v.cans ?? 0;
      return Math.max(max, start + every * Math.max(0, cans - 1));
    }, 0);
  const defaultMax = Math.min(400, lastEmission + 2 * raw.grid.width * raw.grid.height + 10);

  const level: Level = {
    id: raw.id,
    title: raw.title,
    mode: raw.mode as Mode,
    grid: raw.grid,
    tickMs: raw.tick_ms ?? 400,
    maxTicks: raw.max_ticks ?? defaultMax,
    fixed,
    starters,
    inventory: raw.inventory,
  };
  if (raw.hint !== undefined) level.hint = raw.hint;
  if (raw.tutorial !== undefined) level.tutorial = raw.tutorial;
  return level;
}

export function referenceSolution(raw: RawLevel): Placement[] {
  return (raw.reference_solution ?? []).map(toPlacement);
}
